<script lang="ts" setup>
import { ref, computed, watch } from 'vue';

import { sendMessage } from '@/api/messaging';
import { useUserStore } from '@/stores/user';
import { useModalsStore } from '@/stores/modals';
import { useAlertsStore } from '@/stores/alerts';
import { usePerformerStore } from '@/stores/performer';
import i18n from '@/translations';

const { openMessage } = useAlertsStore();
const { isLoggedIn } = useUserStore();
const { toggleModal } = useModalsStore();
const props = defineProps<{ performer: any }>();

const form = ref({ content: '' });
const status = ref('idle');
const maxCharacters = 500;

const validateForm = computed(() => {
    return form.value.content && form.value.content.length > 0;
});

const characterCount = computed(() => form.value.content.length);

watch(() => form.value.content, (newValue) => {
    if (newValue.length > maxCharacters) {
        form.value.content = newValue.slice(0, maxCharacters);
    }
});

async function unBlock(){
        if (!props.performer || !props.performer.id){
            return;
        }

        usePerformerStore().toggleBlock(props.performer.id);

        useAlertsStore().openMessage( 
            { 
                content: i18n.global.t(
                    'account.inbox.unblocked',
                    {name: props.performer!.nickname} 
                ),
                class: 'success',
                translate: false
            }
        )
    }

async function sendMail(){
    if(status.value === 'sending'){ return; }

    const message = {
        account_id: props.performer.id,
        content: form.value.content,
        type: 'email',
        reply_id: 0
    };

    status.value = 'sending';
    const { error, result } = await sendMessage((message as any));

    if (error) {
        if (error.code === 409 && ['Performer block', 'Client has insufficient fonds'].includes(error.message)) {
            openMessage({
                content: `account.alerts.error${error.message.replace(/\s/g, '')}`,
                class: 'error'
            });
        } else {
            openMessage({
                content: 'account.alerts.errorMessageNotSend',
                class: 'error'
            });
        }
        status.value = 'idle';
        return error;
    }

    openMessage({
        content: 'account.alerts.successMessageSend',
        class: 'success'
    });
    form.value = { content: '' };
    status.value = 'idle';
}

</script>

<template>
    <div class="tabs__content">
        <div  class="tabs__content-colum1">
            <h2>{{ $t('tabs.calltitle') }} <span>{{ performer.nickname }} </span> {{ $t('tabs.mailtitle') }}</h2>
            <div v-if="isLoggedIn()">
                <div class="form__group form__group-small">
                    <textarea v-model="form.content" class="form__message" :disabled="performer && performer.isBlocked" maxlength="500" cols="2" :placeholder="performer && performer.isBlocked ? $t('profile.session.canttype') : $t('tabs.mailmessage')" ></textarea>
                    <div class="form__message-count">{{ characterCount }} / {{ maxCharacters }}</div>
                </div>
                <p>{{ $t('tabs.mailinfo') }}</p>
                <div class="tabs__content-bottom">
                    <button v-if="performer && performer.isBlocked" type="button" class="btn btn-full btn-small btn-green" @click="unBlock">{{  $t("account.inbox.unblockToSend") }}</button>
                    <button  v-else class="btn btn-full btn-small btn-green" :class="{'disabled': !validateForm}" @click="sendMail()">{{ $t('tabs.mailsend') }}</button>
                </div>
            </div>
            <div v-if="!isLoggedIn()">
                <p>{{ $t('tabs.mailloggedin') }}</p>
                <a class="btn btn-small btn-green" @click="toggleModal('login')">{{ $t('header.user.login') }}</a>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
@use '~theme' as *;
@use '@/styles/_mixins.scss' as *;

.form__message {
    font-family: inherit;
    height: 70px;
    font-weight: 600;
    resize: vertical;

    &-count {
        color: $pallete-1;
        line-height: normal;
        text-align: right;
        @include rem(font-size, 8px);
    }
}
</style>